import React, { useMemo, useCallback, useState, useLayoutEffect, useEffect, useRef } from 'react';
import queryString from 'query-string';
import {
  makeStyles,
  mergeClasses,
  Subtitle1, Subtitle2, Button,
} from '@fluentui/react-components';
import {
  ArrowReset24Regular,
} from '@fluentui/react-icons';
import {
  HollowSparkleSVGIcon,
  HiddenDivForAnnounce,
} from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { usePageActiveTimeTracker } from '@bingads-webui-campaign-react/common-hooks';
import { Mode } from '@bingads-webui-react/app-layout-container';

import { useGenerateImagePageState, statuses } from './state';
import { BrandDetectionConfirmation } from './brand-detection-confirmation';
import { PromptEditor } from './prompt-editor';
import { GeneratedImages } from './generated-images';
import { ExampleImage } from './example-image';
import { ExplorePrompts } from './explore-prompts';
import { AIGCDisclaimer } from './disclaimer';
import { RemainingText } from './remaing-text';
import { ErrorMessage } from './error';
import { useGenerateImagePageStyles } from './style';

const ActionBar = (props) => {
  const {
    i18n,
    startOver,
    generateImage,
    isLoading,
    isValid,
  } = props;

  const onStartOverKeyDown = (evt) => {
    if (evt.code === 'Enter' || evt.code === 'Space') {
      startOver(evt);
      evt.stopPropagation();
      evt.preventDefault();
    }
  };

  const onGenerateImageKeyDown = (evt) => {
    if (evt.code === 'Enter' || evt.code === 'Space') {
      generateImage(evt);
      evt.stopPropagation();
      evt.preventDefault();
    }
  };

  return (
    <div className="action-bar">
      <Button
        className="start-over-button"
        icon={<ArrowReset24Regular />}
        onClick={startOver}
        onKeyDown={onStartOverKeyDown}
        disabled={!isValid || isLoading}
      >
        <Subtitle2>
          {i18n.getString(_TL_('Start over'))}
        </Subtitle2>
      </Button>
      <Button
        className="generate-button"
        icon={<HollowSparkleSVGIcon className="sparkle-icon" />}
        onClick={generateImage}
        onKeyDown={onGenerateImageKeyDown}
        disabled={!isValid || isLoading}
      >
        <Subtitle2>
          {i18n.getString(_TL_('Generate'))}
        </Subtitle2>
      </Button>
    </div>
  );
};

const useTopDivMetaData = (topDiv) => {
  const [offset, setOffset] =
    useState(0);

  useLayoutEffect(() => {
    if (topDiv) {
      let target = topDiv;
      let newOffset = 0;
      do {
        newOffset += target.offsetTop;
        target = target.offsetParent;
      } while (target);

      if (newOffset !== offset) {
        setOffset(newOffset);
      }
    }
  }, [offset, topDiv]);

  return [offset];
};

const PromptPanel = (props) => {
  const {
    i18n,
    setPrompt,
    prompt,
    startOver,
    generateImage,
    detectedBrandName,
    renderHelpButton,
    onGenerateImage,
    logger,
    isLoading,
    promptError,
    serverError,
    isValid,
    textAreaRef,
  } = props;

  const [topDiv, setTopDiv] = useState(null);
  const [offset] = useTopDivMetaData(topDiv);

  return (
    <div className="prompt-panel" ref={ref => setTopDiv(ref)} style={{ height: `calc(100% - 33px - ${offset}px)` }}>
      <div className="title">
        <Subtitle1 role="heading" aria-level="1">{i18n.getString(_TL_('Generate image'))}</Subtitle1>
      </div>
      <RemainingText i18n={i18n} className="remainingText" />
      {!detectedBrandName && (
        <div className="prompt-section">
          <PromptEditor setPrompt={setPrompt} prompt={prompt} i18n={i18n} logger={logger} textAreaRef={textAreaRef} />
          <ActionBar
            i18n={i18n}
            startOver={startOver}
            generateImage={generateImage}
            isLoading={isLoading}
            isValid={isValid}
          />
          <div className="message-bar">
            <AIGCDisclaimer i18n={i18n} />
            <ErrorMessage error={promptError} />
            <ErrorMessage error={serverError} />
          </div>
        </div>
      )}
      {detectedBrandName && (
        <BrandDetectionConfirmation
          i18n={i18n}
          brandName={detectedBrandName}
          onConfirm={() => onGenerateImage(true)}
          logger={logger}
          renderHelpButton={renderHelpButton}
        />
      )}
    </div>
  );
};

const useStyles = makeStyles({
  hidden: {
    display: 'none',
  },
});

const ImagePanel = (props) => {
  const {
    i18n,
    generateImageExample,
    setPrompt,
    MultiLineEllipsisText,
    isLoading,
    stockImages,
    aigcImages,
    loadMoreStockImages,
    totalStockImagesCount,
    addAIGCImage,
    addStockImage,
    cancel,
    savedAIGCImages,
    savedStockImages,
    savingAIGCImages,
    savingStockImages,
    scrollContainer,
    logger,
    stopGeneratingBtnRef,
  } = props;

  const classes = useStyles();
  const hasAIGCImages = (aigcImages && aigcImages.length > 0);
  const hasAIGCImageExample = (generateImageExample && generateImageExample.imageUrl);

  return (
    <div className="image-panel">
      <ExampleImage
        i18n={i18n}
        generateImageExample={generateImageExample}
        classes={
          hasAIGCImageExample && (!hasAIGCImages && !isLoading) ? '' : classes.hidden}
      />
      <div className={mergeClasses('explore-prompts-view', hasAIGCImageExample || hasAIGCImages || isLoading ? classes.hidden : '')}>
        <ExplorePrompts
          scrollContainer={scrollContainer}
          i18n={i18n}
          setPrompt={setPrompt}
          MultiLineEllipsisText={MultiLineEllipsisText}
          logger={logger}
        />
      </div>
      <div className={mergeClasses('images-view', hasAIGCImages || isLoading ? '' : classes.hidden)}>
        <GeneratedImages
          scrollContainer={scrollContainer}
          aigcImages={aigcImages}
          cancel={cancel}
          i18n={i18n}
          savedAIGCImages={savedAIGCImages}
          savedStockImages={savedStockImages}
          savingAIGCImages={savingAIGCImages}
          savingStockImages={savingStockImages}
          isLoading={isLoading}
          stockImages={stockImages}
          totalStockImagesCount={totalStockImagesCount}
          loadMoreStockImages={loadMoreStockImages}
          addAIGCImage={addAIGCImage}
          addStockImage={addStockImage}
          logger={logger}
          stopGeneratingBtnRef={stopGeneratingBtnRef}
        />
      </div>
    </div>
  );
};

export const GenerateImagePageComponent = (props) => {
  const {
    i18n,
    dataService,
    logger,
    renderHelpButton,
    MultiLineEllipsisText,
    notificationApi,
    aid,
    search,
    scrollContainer,
    pagePerfMarker: generateImagePerfMarker,
    changeLayout,
    setDataLoaded,
  } = props;
  const textAreaRef = useRef(null);
  const stopGeneratingBtnRef = useRef(null);
  const [announcedText, setAnnouncedText] = useState('');
  const queries = queryString.parse(search);
  const {
    folderId = '',
  } = queries || {};

  const {
    prompt,
    setPrompt,
    detectedBrandName,
    getImages,
    setDetectedBrandName,
    state,
    loadStockImages,
    loadMoreStockImages,
    totalStockImagesCount,
    cancel,
    aigcImages,
    stockImages,
    addAIGCImage,
    addStockImage,
    isValid,
    promptError,
    serverError,
    savedAIGCImages,
    savedStockImages,
    savingAIGCImages,
    savingStockImages,
    onStartOver,
    generateImageExample,
  } = useGenerateImagePageState({
    logger,
    i18n,
    dataService,
    aid,
    notificationApi,
    folderId,
    perfMarker: generateImagePerfMarker,
  });

  const onGenerateImage = useCallback((skipBrandDetection) => {
    loadStockImages();
    getImages(skipBrandDetection);
    setDetectedBrandName(null);
    if (skipBrandDetection) {
      logger.logTrace(`AIGenerateImagesView: skipped detected brand name: ${detectedBrandName}`);
    }
  }, [detectedBrandName, getImages, loadStockImages, logger, setDetectedBrandName]);

  const classes = useGenerateImagePageStyles();
  const startOver = (event) => {
    onStartOver();

    if ((event.code === 'Enter' || event.code === 'Space') && !(generateImageExample && generateImageExample.prompt)) {
      // if no initial prompt, click start over button will clean the prompt text, then the start over button will be disabled, focus on the text area.
      if (textAreaRef && textAreaRef.current) {
        textAreaRef.current.focus();
      }
    }
  };

  const isLoading = useMemo(() => (state.status === statuses.cancelling
    || state.status === statuses.loading), [state]);

  const generateImage = (event) => {
    onGenerateImage();
    setAnnouncedText(i18n.getString(_TL_('Start to generate images')));

    if (event.code === 'Enter' || event.code === 'Space') {
      setTimeout(() => {
        if (stopGeneratingBtnRef && stopGeneratingBtnRef.current) {
          stopGeneratingBtnRef.current.focus();
        }
      });
    }
  };

  usePageActiveTimeTracker(logger, 'Generate-image');

  useEffect(() => {
    if (setDataLoaded && generateImagePerfMarker) {
      generateImagePerfMarker.willRender();
      setDataLoaded();
    }
  }, [generateImagePerfMarker, setDataLoaded]);

  useEffect(() => {
    setClarityTag({ LoadAdsStudioImageGenerationPage: true });
  }, []);

  useLayoutEffect(() => {
    if (changeLayout) {
      changeLayout(
        {
          nav: {
            mode: Mode.Hidden,
          },
        },
        true, // isPageConfig
        true // isPageConfigPartialUpdate
      );
    }
  }, [changeLayout]);

  return (
    <div className={mergeClasses('generate-image-page', classes)}>
      <PromptPanel
        i18n={i18n}
        prompt={prompt}
        setPrompt={setPrompt}
        startOver={startOver}
        generateImage={generateImage}
        isLoading={isLoading}
        detectedBrandName={detectedBrandName}
        renderHelpButton={renderHelpButton}
        logger={logger}
        promptError={promptError}
        serverError={serverError}
        isValid={isValid}
        onGenerateImage={onGenerateImage}
        textAreaRef={textAreaRef}
      />
      <ImagePanel
        scrollContainer={scrollContainer}
        i18n={i18n}
        generateImageExample={generateImageExample}
        MultiLineEllipsisText={MultiLineEllipsisText}
        setPrompt={setPrompt}
        cancel={cancel}
        isLoading={isLoading}
        totalStockImagesCount={totalStockImagesCount}
        loadMoreStockImages={loadMoreStockImages}
        stockImages={stockImages}
        aigcImages={aigcImages}
        addAIGCImage={addAIGCImage}
        addStockImage={addStockImage}
        savedAIGCImages={savedAIGCImages}
        savedStockImages={savedStockImages}
        savingAIGCImages={savingAIGCImages}
        savingStockImages={savingStockImages}
        logger={logger}
        stopGeneratingBtnRef={stopGeneratingBtnRef}
      />
      <HiddenDivForAnnounce toAnnounce={announcedText} />
    </div>);
};
